import * as React from "react";
import { observer } from "mobx-react";

import { Viewport, ViewSmall, ViewMedium, ViewLarge } from "bernie-view";

import { UitkCardAloha, UitkCardTextSegment } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkFigureAspectRatioType as ExperimentalAspectRatioType } from "uitk-react-experimental-images";
import { UitkScrimType as UitkExperimentalScrimType } from "uitk-react-experimental-scrim";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

import { ScrimCard } from "components/shared/ScrimCard/ScrimCard";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";

import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";

import Campaign = CampaignRecommendationsWithOffersQuery.Campaigns;

export interface CampaignVRProps {
  campaign: Campaign;
  campaignTitle?: string;
  imgSrc?: string;
  context?: ExtendedContextStore;
}

export const CampaignVR = observer(({ campaign, campaignTitle, imgSrc, context }: CampaignVRProps) => {
  const campaignHeading = campaignTitle || campaign.headline;
  const image = imgSrc || "";

  const scrimCardPropsSmall = {
    id: "vr-campaign-header",
    dataTestId: "vr-campaign-header",
    className: "",
    titleSlot: <h3 className="uitk-type-700">{campaignHeading}</h3>,
    subtitleSlot: (
      <UitkLayoutFlex direction="column" justifyContent="space-between" style={{ height: "inherit" }}>
        <UitkSpacing padding={{ small: { blockstart: undefined } }}>
          <UitkLayoutFlexItem>
            <div>
              <UitkText size={500}>{campaign.description.long}</UitkText>
            </div>
          </UitkLayoutFlexItem>
        </UitkSpacing>
      </UitkLayoutFlex>
    ),
  };

  const mediumClassName = "vrScrim";
  const mediumSpacing: UitkSpacingProps = {
    padding: { blockend: undefined },
    margin: { blockstart: "three" },
  };
  const titleGrid: UitkLayoutConditionalGridColumnRow = { medium: 12 };
  const titleColSpan: UitkLayoutConditionalGridSpan = { medium: 7, large: 3 };

  const scrimCardPropsMedium = {
    id: "vr-campaign-header",
    dataTestId: "vr-campaign-header",
    className: mediumClassName,
    titleSlot: (
      <UitkLayoutGrid columns={titleGrid} space="two">
        <UitkLayoutGridItem colSpan={titleColSpan}>
          <UitkSpacing padding={{ inlinestart: "six" }}>
            <div>
              <Viewport>
                <ViewMedium>
                  <h3 className="uitk-type-700">{campaignHeading}</h3>
                </ViewMedium>
                <ViewLarge>
                  <h3 className="uitk-type-800">{campaignHeading}</h3>
                </ViewLarge>
              </Viewport>
            </div>
          </UitkSpacing>
        </UitkLayoutGridItem>
      </UitkLayoutGrid>
    ),
    footerSlot: (
      <UitkCardAloha className="vrFooter uitk-card-aloha-roundcorner-top-right uitk-card-aloha-roundcorner-top-left uitk-flex">
        <UitkCardTextSegment>
          <UitkSpacing padding={{ inlinestart: "three", block: "three" }}>
            <span>{campaign.description.long}</span>
          </UitkSpacing>
        </UitkCardTextSegment>
      </UitkCardAloha>
    ),
    spacing: mediumSpacing,
  };

  const isVariantScrimTestEnabled = isVariantEnabled(context, Experiment.PWA_banner_VR_scrims_test);
  const scrimTypeForSmallView = isVariantScrimTestEnabled
    ? UitkExperimentalScrimType.OVERLAY_TOP
    : UitkExperimentalScrimType.OVERLAY;
  const scrimTypeForMediumAndBiggerViews = isVariantScrimTestEnabled
    ? UitkExperimentalScrimType.OVERLAY_LEFT
    : UitkExperimentalScrimType.OVERLAY;

  return (
    <Viewport>
      <ViewSmall>
        <ScrimCard
          {...scrimCardPropsSmall}
          aspectRatio={ExperimentalAspectRatioType.R1_1}
          backgroundImageURL={image}
          scrimType={scrimTypeForSmallView}
        />
      </ViewSmall>
      <ViewMedium>
        <ScrimCard
          {...scrimCardPropsMedium}
          aspectRatio={ExperimentalAspectRatioType.R4_1}
          backgroundImageURL={image}
          scrimType={scrimTypeForMediumAndBiggerViews}
        />
      </ViewMedium>
    </Viewport>
  );
});
